import { useRef, useId, type ReactNode, type KeyboardEvent } from 'react';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { CaretIcon } from '@/UI/Icons/CaretIcon';
import { color } from '@/colors';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { PARENT_MENU_ITEM_DATA_ATTRIBUTE, useParentMenuHelper } from './hooks/useParentMenuHelper';
import type { SxProps, Theme } from '@mui/material';

interface ParentMenuItemProps {
  label: string;
  children: ReactNode;
  id?: string;
  onClose?: () => void;
  contextMenuWidth?: string;
  offset?: { top?: string; left?: string };
  sticky?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  onKeyDown?: (e: KeyboardEvent) => void;
}

const ParentMenuItem = ({
  label,
  children,
  id,
  onClose,
  contextMenuWidth,
  offset,
  sticky,
  disabled = false,
  sx,
  autoFocus = true,
  onKeyDown,
}: ParentMenuItemProps) => {
  const menuItemRef = useRef<HTMLLIElement>(null);
  const menuItemId = useId();
  const normMenuItemId = id ?? menuItemId;

  const { close, isOpen, shouldFocusOnExit, handleMouseEnter, handleMouseLeave, handleItemKeyDown, handleMenuKeyDown } =
    useParentMenuHelper({
      menuItemRef,
      sticky,
      onKeyDown,
    });

  const onMenuClose = () => {
    close();
    onClose?.();
  };

  return (
    <AppMenuItem
      onKeyDown={handleItemKeyDown}
      ref={menuItemRef}
      sx={{
        ...(isOpen && {
          backgroundColor: `${color.Black84} !important`,
        }),
        ...sx,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      id={normMenuItemId}
      disabled={disabled}
      {...{ [PARENT_MENU_ITEM_DATA_ATTRIBUTE]: 'true' }}
    >
      <EllipsisText sx={{ flex: 1 }} variant="body-sm-rg" placement="bottom">
        {label}
      </EllipsisText>
      <CaretIcon width={12} height={12} style={{ transform: 'rotate(-90deg)' }} />
      <AppContextMenu
        slotProps={{
          transition: {
            onEntered: (node) => {
              if (node && autoFocus) {
                const firstMenuItem = node.querySelector('li');
                firstMenuItem?.focus();
              }
            },
            onExited: () => {
              if (shouldFocusOnExit) {
                menuItemRef.current?.focus();
              }
            },
          },
        }}
        disableRestoreFocus
        onKeyDown={handleMenuKeyDown}
        sx={{
          pointerEvents: 'none',
          '& .MuiList-root': {
            pointerEvents: 'auto',
          },
        }}
        MenuListProps={{ 'aria-labelledby': normMenuItemId }}
        anchorEl={menuItemRef.current}
        open={isOpen}
        onClose={onMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        width={contextMenuWidth}
        offset={offset}
      >
        {children}
      </AppContextMenu>
    </AppMenuItem>
  );
};

export default ParentMenuItem;
